import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MessageBubble from './MessageBubble';
import CrisisBanner from './CrisisBanner';
import LoadingIndicator from './LoadingIndicator';
import { useAuth } from '../contexts/AuthContext';
import useStreamingChat from '../hooks/useStreamingChat';
import useTranscribeVoiceInput from '../hooks/useTranscribeVoiceInput';
import usePollyTTS from '../hooks/usePollyTTS';
import useDemoChat from '../hooks/useDemoChat';
import { useDemoMode } from '../contexts/DemoModeContext';
import { DEMO_ENTRIES, DEMO_GROUPS, DEMO_STUDENT, DemoGroup } from '../demo/demoScript';
import { HIDE_BETA_FEATURES, BETA_MODE } from '../config/featureFlags';
import type { Message } from '../types/chat';

interface ChatWindowProps {
  sessionId: string | null;
  onSessionCreated: (sessionId: string) => void;
}

export default function ChatWindow({ sessionId, onSessionCreated }: ChatWindowProps) {
  const { t } = useTranslation('chat');
  const { idToken, impersonatedStudentId, preferredLanguage } = useAuth();
  const { demoMode } = useDemoMode();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showCrisisBanner, setShowCrisisBanner] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const streamingMsgIdRef = useRef<string | null>(null);

  const {
    connect,
    disconnect,
    sendMessage: wsSendMessage,
    isConnected,
    isStreaming: wsIsStreaming,
    streamedText,
    lastComplete,
    lastError,
    statusMessage,
    resetStream,
  } = useStreamingChat();

  const { isListening, isSupported, error: voiceError, startListening, stopListening } = useTranscribeVoiceInput({
    onTranscript: (text) => setInput((prev) => (prev ? prev + ' ' + text : text)),
    idToken,
  });

  const { ttsEnabled, toggleTTS, isSpeaking, speakFullText, stop: stopTTS, playbackSpeed, setPlaybackSpeed } = usePollyTTS({ idToken, language: preferredLanguage });

  // Demo Mode: scripted, client-side replies (dev only). See demo/demoScript.ts.
  const { runDemoReply, demoStatus, demoPhase } = useDemoChat({
    setMessages,
    onCrisis: () => setShowCrisisBanner(true),
  });

  const SPEED_OPTIONS = [1, 1.5, 2, 2.5];
  const cycleSpeed = () => {
    const currentIndex = SPEED_OPTIONS.indexOf(playbackSpeed);
    const nextIndex = (currentIndex + 1) % SPEED_OPTIONS.length;
    setPlaybackSpeed(SPEED_OPTIONS[nextIndex]);
  };

  // Connect WebSocket when authenticated
  useEffect(() => {
    if (!idToken) return;

    const wsUrl = window.__PAILA_CONFIG__?.wsUrl || import.meta.env.VITE_WEBSOCKET_URL;
    if (wsUrl) {
      connect(wsUrl, idToken);
    }

    return () => {
      disconnect();
    };
  }, [idToken, connect, disconnect]);

  // Handle streamed text chunks — update the streaming assistant message
  // When a non-English language is selected, skip showing English chunks;
  // the translated response will appear when the 'complete' message arrives.
  useEffect(() => {
    if (!streamingMsgIdRef.current || !streamedText) return;
    if (preferredLanguage && preferredLanguage !== 'en') return;

    const msgId = streamingMsgIdRef.current;
    setMessages((prev) =>
      prev.map((m) =>
        m.id === msgId ? { ...m, content: streamedText, isStreaming: true } : m
      )
    );
  }, [streamedText, preferredLanguage]);

  // Handle complete message — replace with authoritative post-processed text
  useEffect(() => {
    if (!lastComplete) return;

    const msgId = streamingMsgIdRef.current;
    if (msgId) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === msgId
            ? {
                ...m,
                content: lastComplete.response || m.content,
                isStreaming: false,
                isCrisis: lastComplete.crisis_detected,
              }
            : m
        )
      );
    }

    // TTS: speak the authoritative final response only after it's fully complete.
    // Guarded so a stale localStorage `paila-tts-enabled=true` cannot keep voice
    // active on environments where the UI toggle has been hidden.
    if (lastComplete.response && !HIDE_BETA_FEATURES) {
      speakFullText(lastComplete.response);
    }

    // Update session ID if returned
    if (lastComplete.session_id && !sessionId) {
      onSessionCreated(lastComplete.session_id);
    }

    if (lastComplete.crisis_detected) {
      setShowCrisisBanner(true);
    }

    streamingMsgIdRef.current = null;
    setIsLoading(false);
    resetStream();
  }, [lastComplete, sessionId, onSessionCreated, resetStream, speakFullText]);

  // Handle WS error
  useEffect(() => {
    if (!lastError) return;

    const msgId = streamingMsgIdRef.current;
    if (msgId) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === msgId
            ? { ...m, content: lastError, isStreaming: false }
            : m
        )
      );
    }

    streamingMsgIdRef.current = null;
    setIsLoading(false);
    resetStream();
  }, [lastError, resetStream]);

  // Load existing session messages
  useEffect(() => {
    const loadSession = async () => {
      if (!sessionId || !idToken) return;

      try {
        const apiUrl = window.__PAILA_CONFIG__?.apiUrl || import.meta.env.VITE_API_URL || '/api';
        const headers: Record<string, string> = {
          Authorization: `Bearer ${idToken}`,
        };
        if (impersonatedStudentId) {
          headers['X-Impersonate-Student-Id'] = impersonatedStudentId;
        }
        const langParam = preferredLanguage && preferredLanguage !== 'en' ? `?language=${preferredLanguage}` : '';
        const response = await fetch(`${apiUrl}/sessions/${sessionId}${langParam}`, { headers });

        if (response.ok) {
          const data = await response.json();
          const loadedMessages: Message[] = (data.messages || []).map(
            (msg: { role: string; content: string }, index: number) => ({
              id: `${sessionId}-${index}`,
              role: msg.role as 'user' | 'assistant',
              content: msg.content,
              timestamp: new Date(),
            })
          );
          setMessages(loadedMessages);
        }
      } catch (error) {
        console.error('Error loading session:', error);
      }
    };

    loadSession();
  }, [sessionId, idToken, impersonatedStudentId, preferredLanguage]);

  // Clear messages when impersonation changes
  useEffect(() => {
    setMessages([]);
  }, [impersonatedStudentId]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages, isLoading, statusMessage, demoStatus]);

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 200)}px`;
    }
  }, [input]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading || !idToken) return;

    stopTTS();

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    const assistantMsgId = `assistant-${Date.now()}`;

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    // Demo Mode: answer from the script and never touch the backend.
    if (demoMode) {
      await runDemoReply(userMessage.content, assistantMsgId);
      setIsLoading(false);
      return;
    }

    // Try WebSocket first, fall back to REST
    if (isConnected) {
      // Add placeholder assistant message for streaming
      const placeholderMessage: Message = {
        id: assistantMsgId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        isStreaming: true,
      };
      setMessages((prev) => [...prev, placeholderMessage]);
      streamingMsgIdRef.current = assistantMsgId;
      wsSendMessage(userMessage.content, sessionId, impersonatedStudentId, preferredLanguage);
    } else {
      // REST fallback
      try {
        const apiUrl = window.__PAILA_CONFIG__?.apiUrl || import.meta.env.VITE_API_URL || '/api';
        const chatHeaders: Record<string, string> = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        };
        if (impersonatedStudentId) {
          chatHeaders['X-Impersonate-Student-Id'] = impersonatedStudentId;
        }
        const response = await fetch(`${apiUrl}/chat`, {
          method: 'POST',
          headers: chatHeaders,
          body: JSON.stringify({
            message: userMessage.content,
            ...(sessionId && { session_id: sessionId }),
            ...(preferredLanguage !== 'en' && { language: preferredLanguage }),
          }),
        });

        if (response.ok) {
          const data = await response.json();

          if (!sessionId && data.session_id) {
            onSessionCreated(data.session_id);
          }

          const assistantMessage: Message = {
            id: assistantMsgId,
            role: 'assistant',
            content: data.response,
            timestamp: new Date(),
            isCrisis: data.crisis_detected,
          };

          setMessages((prev) => [...prev, assistantMessage]);
          speakFullText(data.response);

          if (data.crisis_detected) {
            setShowCrisisBanner(true);
          }
        } else {
          const errorMessage: Message = {
            id: `error-${Date.now()}`,
            role: 'assistant',
            content: t('errorMessage'),
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, errorMessage]);
        }
      } catch (error) {
        console.error('Error sending message:', error);
        const errorMessage: Message = {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content:
            "I'm sorry, I'm having trouble connecting right now. Please try again in a moment.",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Crisis Banner */}
      {showCrisisBanner && (
        <CrisisBanner onDismiss={() => setShowCrisisBanner(false)} />
      )}

      {/* Demo Mode banner — keeps the audience informed that data is sample data */}
      {demoMode && (
        <div
          className="bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800 px-4 py-1.5 text-center text-xs font-medium text-amber-800 dark:text-amber-200"
          data-testid="demo-banner"
        >
          {t('demoBanner', { name: DEMO_STUDENT.name })}
        </div>
      )}

      {/* Messages Area */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0" aria-live="polite" aria-label="Chat messages">
        {messages.length === 0 ? (
          <WelcomeMessage
            demoMode={demoMode}
            onSuggestionClick={(text) => {
              setInput(text);
              inputRef.current?.focus();
            }}
          />
        ) : (
          messages.map((message, index) => {
            // Find the preceding user message for assistant messages (used for feedback context)
            let precedingUserMessage: string | undefined;
            if (message.role === 'assistant') {
              for (let i = index - 1; i >= 0; i--) {
                if (messages[i].role === 'user') {
                  precedingUserMessage = messages[i].content;
                  break;
                }
              }
            }
            return (
              <MessageBubble
                key={message.id}
                message={message}
                sessionId={demoMode ? 'demo' : sessionId ?? undefined}
                precedingUserMessage={precedingUserMessage}
                demo={demoMode}
              />
            );
          })
        )}

        {/* Show loading indicator only for REST (non-streaming) requests, or status messages */}
        {isLoading && !wsIsStreaming && !streamingMsgIdRef.current && !demoStatus && demoPhase !== 'streaming' && (
          <LoadingIndicator />
        )}

        {/* Show status message (e.g. "Searching academic regulations...") */}
        {(statusMessage || demoStatus) && (
          <div className="flex justify-start" role="status" aria-live="polite">
            <div className="text-sm text-gray-500 dark:text-gray-400 italic px-4 py-2">
              {statusMessage || demoStatus}
            </div>
          </div>
        )}

      </div>

      {/* Input Area */}
      <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-800">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-end space-x-3">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                lang={preferredLanguage}
                aria-label={t('inputAriaLabel')}
                placeholder={t('inputPlaceholder')}
                rows={3}
                className={`w-full px-4 py-3 ${
                  HIDE_BETA_FEATURES
                    ? 'pr-12'
                    : ttsEnabled
                      ? isSupported ? 'pr-48' : 'pr-36'
                      : isSupported ? 'pr-36' : 'pr-24'
                } border border-gray-300 dark:border-gray-600 rounded-xl
                  bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white
                  focus:ring-2 focus:ring-uel-purple-500 focus:border-transparent
                  resize-none transition-all [align-content:center]`}
                disabled={isLoading}
              />
              {/* Voice output (Polly TTS) — hidden when HIDE_BETA_FEATURES is on. */}
              {!HIDE_BETA_FEATURES && (
              <>
              {/* TTS toggle / stop button */}
              <button
                onClick={isSpeaking ? stopTTS : toggleTTS}
                aria-label={isSpeaking ? t('stopAudio') : ttsEnabled ? t('disableVoiceOutput') : t('enableVoiceOutput')}
                aria-pressed={isSpeaking ? undefined : ttsEnabled}
                className={`absolute ${
                  ttsEnabled
                    ? isSupported ? 'right-36' : 'right-24'
                    : isSupported ? 'right-24' : 'right-12'
                } bottom-2 p-2 rounded-lg transition-colors
                  ${
                    isSpeaking
                      ? 'bg-uel-purple-600 text-white animate-pulse-ring'
                      : ttsEnabled
                        ? 'text-uel-purple-600 dark:text-uel-purple-400 hover:text-uel-purple-700 dark:hover:text-uel-purple-300'
                        : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                  }`}
              >
                {isSpeaking ? (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <rect x="6" y="6" width="12" height="12" rx="2" />
                  </svg>
                ) : ttsEnabled ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M15.536 8.464a5 5 0 010 7.072M17.95 6.05a8 8 0 010 11.9M6.5 8.5L11 4v16l-4.5-4.5H3a1 1 0 01-1-1v-5a1 1 0 011-1h3.5z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M6.5 8.5L11 4v16l-4.5-4.5H3a1 1 0 01-1-1v-5a1 1 0 011-1h3.5z" />
                    <line x1="18" y1="9" x2="22" y2="15" strokeWidth={2} strokeLinecap="round" />
                    <line x1="22" y1="9" x2="18" y2="15" strokeWidth={2} strokeLinecap="round" />
                  </svg>
                )}
              </button>
              {/* TTS speed cycle button — only visible when TTS is enabled */}
              {ttsEnabled && (
                <button
                  onClick={cycleSpeed}
                  aria-label={t('playbackSpeed', { speed: playbackSpeed })}
                  className={`absolute ${isSupported ? 'right-24' : 'right-12'} bottom-2 px-1.5 py-2 rounded-lg transition-colors
                    text-xs font-semibold leading-none
                    text-uel-purple-600 dark:text-uel-purple-400 hover:text-uel-purple-700 dark:hover:text-uel-purple-300`}
                >
                  {playbackSpeed}x
                </button>
              )}
              </>
              )}
              {/* Voice input (Transcribe) — hidden when HIDE_BETA_FEATURES is on. */}
              {!HIDE_BETA_FEATURES && isSupported && (
                <button
                  onClick={isListening ? stopListening : startListening}
                  disabled={isLoading}
                  aria-label={isListening ? t('stopVoiceInput') : t('startVoiceInput')}
                  aria-pressed={isListening}
                  className={`absolute right-12 bottom-2 p-2 rounded-lg transition-colors
                    ${
                      isListening
                        ? 'bg-uel-purple-600 text-white animate-pulse-ring'
                        : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                    }
                    disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m-4 0h8M12 1a3 3 0 00-3 3v7a3 3 0 006 0V4a3 3 0 00-3-3z"
                    />
                  </svg>
                </button>
              )}
              <button
                onClick={sendMessage}
                disabled={!input.trim() || isLoading}
                aria-label={t('sendMessage')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg
                  bg-uel-purple-600 text-white
                  hover:bg-uel-purple-700 disabled:opacity-50 disabled:cursor-not-allowed
                  transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                  />
                </svg>
              </button>
            </div>
          </div>
          {!HIDE_BETA_FEATURES && voiceError && (
            <p className="mt-2 text-xs text-red-600 dark:text-red-400 text-center" role="alert">
              {voiceError}
            </p>
          )}
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 text-center">
            {t('disclaimer')}
          </p>
        </div>
      </div>
    </div>
  );
}

function WelcomeMessage({
  onSuggestionClick,
  demoMode = false,
}: {
  onSuggestionClick: (text: string) => void;
  demoMode?: boolean;
}) {
  const { t } = useTranslation('chat');

  const demoGroupTitle: Record<DemoGroup, string> = {
    timetable: t('demoGroupTimetable'),
    record: t('demoGroupRecord'),
    campus: t('demoGroupCampus'),
    about: t('demoGroupAbout'),
  };

  return (
    <div className="max-w-2xl mx-auto text-center py-12">
      <img
        src="/uel-crest.webp"
        alt=""
        aria-hidden="true"
        className="w-20 h-20 object-contain mx-auto mb-6"
      />

      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
        {t('welcomeTitle')}
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-3">
        {t('welcomeSubtitle')}
      </p>
      {BETA_MODE && !demoMode && (
        <p className="inline-flex items-center gap-2 text-xs font-medium mb-8 px-3 py-1.5 rounded-full
          bg-uel-purple-100 text-uel-purple-800
          dark:bg-uel-purple-900/40 dark:text-uel-purple-200">
          <span className="font-bold tracking-wider">BETA</span>
          <span>Built for our first 50 student testers — use 👍 / 👎 below any reply to help us improve.</span>
        </p>
      )}

      {demoMode ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
          {DEMO_GROUPS.map((group) => (
            <SuggestionCard
              key={group.id}
              icon={group.icon}
              title={demoGroupTitle[group.id]}
              suggestions={DEMO_ENTRIES.filter((e) => e.group === group.id).map((e) => e.question)}
              onSuggestionClick={onSuggestionClick}
            />
          ))}
        </div>
      ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
        <SuggestionCard
          icon="📅"
          title={t('sugTimetableTitle')}
          suggestions={[t('sugTimetableQ1'), t('sugTimetableQ2')]}
          onSuggestionClick={onSuggestionClick}
        />
        <SuggestionCard
          icon="📊"
          title={t('sugAttendanceTitle')}
          suggestions={[t('sugAttendanceQ1')]}
          onSuggestionClick={onSuggestionClick}
        />
        <SuggestionCard
          icon="📝"
          title={t('sugAssignmentsTitle')}
          suggestions={[t('sugAssignmentsQ1')]}
          onSuggestionClick={onSuggestionClick}
        />
        <SuggestionCard
          icon="📚"
          title={t('sugPoliciesTitle')}
          suggestions={[t('sugPoliciesQ1')]}
          onSuggestionClick={onSuggestionClick}
        />
      </div>
      )}
    </div>
  );
}

function SuggestionCard({
  icon,
  title,
  suggestions,
  onSuggestionClick,
  disabled,
}: {
  icon: string;
  title: string;
  suggestions: string[];
  onSuggestionClick: (text: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className={`p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl${disabled ? ' opacity-60' : ''}`}>
      <div className="flex items-start space-x-3">
        <span className="text-2xl">{icon}</span>
        <div>
          <h3 className="font-medium text-gray-900 dark:text-white">{title}</h3>
          <div className="mt-1 space-y-1">
            {suggestions.map((s) => (
              disabled ? (
                <span key={s} className="block text-sm text-gray-400 dark:text-gray-500 italic">
                  {s}
                </span>
              ) : (
                <button
                  key={s}
                  type="button"
                  onClick={() => onSuggestionClick(s)}
                  className="block text-sm text-uel-purple-600 dark:text-uel-purple-400 hover:underline cursor-pointer text-left"
                >
                  {s}
                </button>
              )
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
